/**
 * Palworld passive skill (trait) entity database.
 *
 * Same rationale as items-data.ts: "palworld legend passive", "palworld swift
 * runner nimble", "best passives for mining" are individually searched queries
 * that competitor wikis own because every trait has its own URL.
 *
 * Only real in-game passive skills are listed, with their real effect values.
 */

export type PassiveKind = "Combat" | "Work" | "Movement" | "Utility" | "Negative";

export interface Passive {
  slug: string;
  name: string;
  kind: PassiveKind;
  /** Rarity tier as shown by the trait colour in-game. */
  tier: "Legendary" | "Rare" | "Common" | "Negative";
  /** Exact effect, e.g. "Attack +20%, Defense +20%, movement speed +15%". */
  effect: string;
  /** One-sentence answer paragraph. */
  summary: string;
  /** Where it matters most. */
  bestOn: string;
  /** Two practical notes. */
  tips: [string, string];
  /** Entity-specific question. */
  ask: { q: string; a: string };
}

const P = (
  slug: string,
  name: string,
  kind: PassiveKind,
  tier: Passive["tier"],
  effect: string,
  summary: string,
  bestOn: string,
  tips: [string, string],
  ask: { q: string; a: string },
): Passive => ({ slug, name, kind, tier, effect, summary, bestOn, tips, ask });

export const PASSIVES: Passive[] = [
  // ── Combat ─────────────────────────────────────────────────────────────
  P("legend", "Legend", "Combat", "Legendary", "Attack +20%, Defense +20%, movement speed +15%",
    "Legend is Palworld's exclusive legendary-Pal passive, granting +20% Attack, +20% Defense and +15% movement speed.",
    "Jetragon, Frostallion, Necromus, Paladius and other legendary Pals",
    ["Legend cannot be bred onto a non-legendary Pal, so never waste eggs chasing it.",
     "It stacks with Musclehead and Ferocious, which is why legendary damage builds hit so hard."],
    { q: "Can you breed the Legend passive in Palworld?", a: "Only onto legendary Pals. Legend is restricted to Pals that can naturally roll it, so breeding it onto a Lamball or Anubis is impossible." }),
  P("musclehead", "Musclehead", "Combat", "Rare", "Attack +30%, work speed -50%",
    "Musclehead is Palworld's largest single Attack passive at +30%, paid for with a 50% work speed penalty.",
    "Dedicated combat Pals that never work at a base",
    ["Never put Musclehead on a base worker — the work penalty halves its output.",
     "Musclehead + Ferocious + Legend is the standard three-passive damage core."],
    { q: "Is Musclehead good in Palworld?", a: "It is the best damage passive for a pure combat Pal, but the -50% work speed makes it a bad choice for anything you assign to a base." }),
  P("ferocious", "Ferocious", "Combat", "Rare", "Attack +20%",
    "Ferocious grants a flat +20% Attack with no downside, making it the safest damage passive in Palworld.",
    "Any combat Pal or mount you fight from",
    ["Because it has no penalty it is the best passive to keep when breeding a hybrid worker-fighter.",
     "Two Ferocious parents give a strong chance of passing it to the egg."],
    { q: "Ferocious or Musclehead in Palworld?", a: "Musclehead deals more damage (+30%) but halves work speed; Ferocious (+20%) is strictly better on any Pal that also works at your base." }),
  P("burly-body", "Burly Body", "Combat", "Rare", "Defense +20%",
    "Burly Body increases a Pal's Defense by 20% and is the core survivability passive for tanking raid bosses.",
    "Mounts and front-line Pals in raids",
    ["Pair with Hard Skin for +30% total defense on a raid tank.",
     "Defense passives matter more than attack on the Pal you ride into a raid."],
    { q: "What is the best defensive passive in Palworld?", a: "Burly Body at +20% Defense, usually combined with Hard Skin (+10%) on a Pal you use as a raid front-liner." }),
  P("hard-skin", "Hard Skin", "Combat", "Common", "Defense +10%",
    "Hard Skin gives a Pal +10% Defense, the common-tier complement to Burly Body.",
    "Filler slot on tanks and mounts",
    ["Good early filler while you breed toward Burly Body.",
     "Replace it once you have three strong passives competing for slots."],
    { q: "Does Hard Skin stack with Burly Body in Palworld?", a: "Yes — passives of different names stack, so Hard Skin plus Burly Body gives roughly +30% Defense." }),
  P("lord-of-the-underworld", "Lord of the Underworld", "Combat", "Legendary", "Dark attack +20%",
    "Lord of the Underworld boosts a Pal's Dark-element attack damage by 20% in Palworld.",
    "Necromus and other Dark attackers",
    ["Only worth keeping on a Pal whose moveset is actually Dark-element.",
     "Check the type chart before building around it — Dark is resisted by Dragon."],
    { q: "Which Palworld Pals have Lord of the Underworld?", a: "Dark legendaries such as Necromus roll it naturally; it is an element-specific attack passive, not a general damage boost." }),
  P("lord-of-lightning", "Lord of Lightning", "Combat", "Legendary", "Electric attack +20%",
    "Lord of Lightning increases Electric-element attack damage by 20%, the Electric counterpart to Lord of the Underworld.",
    "Orserk, Grizzbolt and Electric attackers",
    ["Pair with an Electric moveset — it does nothing for a Pal's physical attacks.",
     "Electric is the best element against Water Pals, which makes this strong against Jormuntide."],
    { q: "Is Lord of Lightning worth a passive slot in Palworld?", a: "Yes on a dedicated Electric attacker such as Orserk, where the +20% element bonus applies to nearly every skill it uses." }),
  P("divine-dragon", "Divine Dragon", "Combat", "Legendary", "Dragon attack +20%",
    "Divine Dragon adds 20% to a Pal's Dragon-element attack damage in Palworld.",
    "Jetragon, Jormuntide and Dragon attackers",
    ["Dragon hits Dragon hardest, so this is the passive of choice for legendary duels.",
     "Combine with Legend on Jetragon for the highest damage mount in the game."],
    { q: "What does Divine Dragon do in Palworld?", a: "It raises Dragon-element attack damage by 20%, so it only helps Pals whose active skills are Dragon-element." }),
  P("celestial-emperor", "Celestial Emperor", "Combat", "Legendary", "Neutral attack +20%",
    "Celestial Emperor grants +20% Neutral-element attack damage, the rarest of Palworld's element boost passives.",
    "Paladius and Neutral-element attackers",
    ["Neutral has no resistances against it, making this consistently useful in raids.",
     "Good on Paladius, whose charge skills are Neutral-element."],
    { q: "Which element does Celestial Emperor boost in Palworld?", a: "Neutral. Because no element resists Neutral, the bonus applies consistently against every target." }),
  P("vanguard", "Vanguard", "Combat", "Common", "Attack +10%",
    "Vanguard is the common-tier attack passive in Palworld, adding +10% Attack with no penalty.",
    "Early combat Pals and breeding filler",
    ["Use it as a placeholder while breeding toward Ferocious.",
     "Two Vanguard parents can still roll higher-tier passives on the egg."],
    { q: "Is Vanguard worth keeping in Palworld?", a: "Early yes, late no — +10% Attack is fine until you can replace it with Ferocious (+20%) or Musclehead (+30%)." }),
  P("brave", "Brave", "Combat", "Common", "Attack +10%",
    "Brave is a common Palworld passive adding +10% Attack, functionally an alternative roll to Vanguard.",
    "Early-game fighters",
    ["Brave and Vanguard both give +10% and do stack with each other.",
     "Keep whichever you roll first; neither is worth targeted breeding."],
    { q: "Do Brave and Vanguard stack in Palworld?", a: "Yes. They are separate passives, so a Pal with both gains roughly +20% Attack." }),
  P("coward", "Coward", "Negative", "Negative", "Attack -10%",
    "Coward is a negative Palworld passive that reduces Attack by 10% and should be bred out of any combat line.",
    "Nothing — breed it out",
    ["A parent with Coward can pass it on; retire it from your breeding pairs.",
     "It is harmless on a pure base worker, so use those Pals as ranch fodder."],
    { q: "How do you remove a bad passive in Palworld?", a: "You cannot remove passives directly — breed the Pal again and keep the offspring that inherits only the traits you want." }),

  // ── Movement ───────────────────────────────────────────────────────────
  P("swift", "Swift", "Movement", "Rare", "Movement speed +30%",
    "Swift is Palworld's strongest single movement passive at +30% speed, the core of every fast-mount build.",
    "Flying and ground mounts",
    ["Swift + Runner + Nimble is the classic +50% speed mount stack.",
     "Speed passives apply while you ride, which is why Jetragon builds always chase them."],
    { q: "What is the best speed passive in Palworld?", a: "Swift at +30%, usually stacked with Runner (+20%) and Nimble (+10%) for the fastest possible mount." }),
  P("runner", "Runner", "Movement", "Common", "Movement speed +20%",
    "Runner adds +20% movement speed and is the second piece of Palworld's standard mount speed stack.",
    "Any mount you travel on",
    ["Breed Runner onto your travel mount before chasing combat passives.",
     "It works on ground mounts and flyers equally."],
    { q: "Do movement passives affect mount speed in Palworld?", a: "Yes — Swift, Runner and Nimble all increase the speed of the Pal while you are riding it, which is why they are bred onto Jetragon." }),
  P("nimble", "Nimble", "Movement", "Common", "Movement speed +10%",
    "Nimble is the common +10% movement speed passive, the third slot in a full Palworld speed build.",
    "Travel mounts",
    ["Cheapest of the three speed passives to breed and easy to find on wild Pals.",
     "Drop it last if you need a slot for a combat passive."],
    { q: "Can you have Swift, Runner and Nimble on one Pal in Palworld?", a: "Yes. All three stack for roughly +60% movement speed, and that combination is the standard target when breeding a Jetragon mount." }),
  P("legs-of-the-lion", "Legs of the Lion", "Movement", "Legendary", "Movement speed +15%",
    "Legs of the Lion is a legendary-tier Palworld passive that raises movement speed by 15%.",
    "Legendary mounts",
    ["Rolls naturally on legendary Pals alongside Legend.",
     "Stacks with Swift, Runner and Nimble for extreme travel speed."],
    { q: "Is Legs of the Lion better than Swift in Palworld?", a: "No — Swift gives +30% versus +15%, but they stack, so a mount with both is faster than either alone." }),

  // ── Work ───────────────────────────────────────────────────────────────
  P("artisan", "Artisan", "Work", "Rare", "Work speed +50%",
    "Artisan is Palworld's best work passive, raising a base Pal's work speed by 50%.",
    "Every base worker, especially Handiwork and Kindling Pals",
    ["Artisan on an Anubis is the single biggest crafting-throughput upgrade in the game.",
     "Breed Artisan into your worker lines before chasing combat traits."],
    { q: "What is the best work passive in Palworld?", a: "Artisan at +50% work speed, ideally stacked with Serious (+20%) and Lucky (+15%) on a high-suitability base Pal." }),
  P("serious", "Serious", "Work", "Common", "Work speed +20%",
    "Serious increases a Pal's work speed by 20% and is the reliable common-tier base worker passive in Palworld.",
    "Base workers of every suitability",
    ["Easy to find on wild Pals, so it is the cheapest work upgrade available.",
     "Stacks with Artisan for +70% combined work speed."],
    { q: "Does Serious stack with Artisan in Palworld?", a: "Yes — different passives always stack, so Artisan plus Serious gives about +70% work speed on the same Pal." }),
  P("lucky", "Lucky", "Work", "Rare", "Work speed +15%, Attack +15%",
    "Lucky is Palworld's hybrid passive, giving both +15% work speed and +15% Attack with no drawback.",
    "Hybrid Pals that fight and work",
    ["The best passive for a Pal you both ride and assign to a base.",
     "Keep Lucky when breeding hybrids; it saves a slot versus one work and one combat trait."],
    { q: "Is Lucky a good passive in Palworld?", a: "Yes — it is one of the few passives that improves both combat and base work, making it ideal for Pals doing double duty." }),
  P("work-slave", "Work Slave", "Work", "Common", "Work speed +30%, Attack -30%",
    "Work Slave trades 30% Attack for 30% work speed, making it a pure base-worker passive in Palworld.",
    "Pals that never leave the base",
    ["Excellent on ranch and furnace Pals; terrible on anything you fight with.",
     "Combine with Artisan and Serious for a maximum-throughput crafter."],
    { q: "Is Work Slave bad in Palworld?", a: "Not on a base worker — the -30% Attack is irrelevant for a Pal that only crafts, and +30% work speed is a large gain." }),
  P("conceited", "Conceited", "Work", "Common", "Work speed +15%, Defense -10%",
    "Conceited gives +15% work speed at the cost of 10% Defense, a mild trade for base-only Pals.",
    "Base workers you never take into combat",
    ["Fine on a furnace or plantation Pal that never takes damage.",
     "Avoid it on raid defenders — base raids do damage your workers."],
    { q: "Should you keep Conceited in Palworld?", a: "Keep it on a base-only worker. If your base gets raided often, the -10% Defense makes it a worse pick than Serious." }),
  P("positive-thinker", "Positive Thinker", "Utility", "Common", "SAN decrease rate reduced",
    "Positive Thinker slows how fast a Pal's SAN drops, keeping base workers productive for longer in Palworld.",
    "Long-shift base workers",
    ["Combine with a Hot Spring and Pal Beds and your base practically never stalls.",
     "Most valuable on Pals working heat-intensive stations such as furnaces."],
    { q: "What lowers SAN in Palworld?", a: "Overwork, hunger, injury and missing beds. Positive Thinker slows the drop, while Hot Springs, food and Pal Beds restore it." }),
  P("diet-lover", "Diet Lover", "Utility", "Common", "Hunger rate +15%",
    "Diet Lover makes a Pal eat 15% faster, a minor negative side effect on otherwise strong Pals in Palworld.",
    "Acceptable on any Pal with a well-stocked Feed Box",
    ["Harmless if your food production is automated; costly if it is not.",
     "Prioritise removing it only after you have your three good passives."],
    { q: "Is Diet Lover bad in Palworld?", a: "Only mildly. It raises hunger by 15%, which matters only if your Feed Box runs dry — automate Berry or Wheat production and it is a non-issue." }),
  P("clumsy", "Clumsy", "Negative", "Negative", "Work speed -10%",
    "Clumsy is a negative Palworld passive that reduces work speed by 10% and should be bred out of worker lines.",
    "Nothing — breed it out",
    ["Retire Clumsy parents from worker breeding pairs.",
     "It is harmless on a pure combat mount."],
    { q: "Can you reroll passives in Palworld?", a: "Not on an existing Pal. You breed again and keep the offspring with the better trait set, which is why players keep several parents per line." }),
  P("infinite-stamina", "Infinite Stamina", "Utility", "Rare", "Stamina no longer decreases",
    "Infinite Stamina removes a Pal's stamina drain entirely, making it the single best travel passive in Palworld.",
    "Flying mounts used for long-distance travel",
    ["On a flyer it means non-stop flight across Palpagos with no forced landings.",
     "Pair with Swift and Runner for the definitive travel mount."],
    { q: "Does Infinite Stamina work on flying mounts in Palworld?", a: "Yes — it is most valuable there, because a flyer with Infinite Stamina never has to land to recover stamina mid-journey." }),
  P("motivational-leader", "Motivational Leader", "Utility", "Rare", "Player attack up while mounted",
    "Motivational Leader raises the player's own attack while riding the Pal that carries it.",
    "Mounts you shoot from",
    ["Best on a flying mount you fight from with a rifle.",
     "The bonus applies to your weapon damage, not the Pal's skills."],
    { q: "What does Motivational Leader do in Palworld?", a: "It increases the player's attack damage while mounted on that Pal, so it pays off on any mount you shoot from during raids." }),
  P("aggressive", "Aggressive", "Combat", "Common", "Attack +10%, Defense -10%",
    "Aggressive trades 10% Defense for 10% Attack, a filler combat passive in Palworld.",
    "Glass-cannon combat Pals",
    ["Acceptable filler on a Pal that stays behind your own fire.",
     "Replace with Ferocious as soon as you can breed it."],
    { q: "Is Aggressive worth using in Palworld?", a: "As early filler only — the net gain is small, so it is usually replaced once Ferocious or Musclehead is available." }),
];

export const PASSIVE_BY_SLUG: Record<string, Passive> = Object.fromEntries(
  PASSIVES.map((p) => [p.slug, p]),
);

export const PASSIVE_KINDS: PassiveKind[] = ["Combat", "Work", "Movement", "Utility", "Negative"];
